// import "./wdyr";

import "./lib/posthog"; // Initialize PostHog (side-effect import)
import ReactDOM from "react-dom/client";
import reportWebVitals from "./reportWebVitals";
import {
  hydrateLoginMode,
  shouldAwaitFreshLoginMode,
  primeLoginModeFromCache,
  refreshCachedLoginMode,
} from "./helpers/loginConfig";
import { getTenant } from "./helpers/tenant";
import { hasStoredUser } from "./helpers/authStorage";

const root = ReactDOM.createRoot(document.getElementById("root")!);

/**
 * App is imported dynamically because its module scope runs
 * initializeSuperTokens(), which reads isSSOEnabled() — so the server-driven
 * login mode has to be settled first.
 *
 * The network call is only awaited where it changes what renders:
 *   * auth routes (/login, /register, …) pick between the SuperTokens UI and
 *     the email/password form, and nothing else is loading at that point;
 *   * a first-ever visit has no cached answer to fall back on.
 * Every other page load primes from localStorage synchronously, so an
 * already-signed-in user pays no extra round trip before the app appears. The
 * cache is then kept fresh by whichever source is cheaper: /auth/tenant-info
 * for a signed-in user (a request they already make — see useAppActions), or a
 * post-render refresh for a signed-out one.
 */
const bootstrap = async () => {
  const tenant = getTenant();
  const hasCachedLoginMode = primeLoginModeFromCache(tenant);
  const mustAwaitFreshLoginMode = shouldAwaitFreshLoginMode(
    hasCachedLoginMode,
    hasStoredUser(),
    window.location.pathname
  );

  if (mustAwaitFreshLoginMode) {
    // Never rejects; times out in 3s, so this cannot dead-end the page.
    await hydrateLoginMode(tenant);
  }

  const { default: App } = await import("./App");
  // Strict mode creates problems for code editor
  root.render(<App />);

  if (!mustAwaitFreshLoginMode && !hasStoredUser()) {
    // Signed out, so no tenant-info fetch is coming to refresh the cache.
    // Cache-only, applied on the next load — see refreshCachedLoginMode.
    void refreshCachedLoginMode(tenant);
  }
};

void bootstrap();

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
